import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Loader2, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : 'heute');
const SIZES = [25, 50, 100];

// Letters go to the post office in batches of 50 → one letter date per block of 50 first letters, in writing
// order (series by series, alphabetical #1…n), plus a head count per date (Talu, 10.10.2026).
export const DateBlocksEditor = ({ kind = 'standard', onChanged }) => {
  const [size, setSize] = useState(50);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    try { setData((await api.getPrintQueueDateBlocks(kind, size)).data); }
    catch { setData(null); }
  }, [kind, size]);
  useEffect(() => { load(); }, [load]);

  const setBlock = async (idx, val) => {
    setBusy(String(idx));
    try {
      const r = await api.setPrintQueueDateBlocks(kind, size, { [idx]: val || '' });
      setData(r.data);
      const b = r.data.blocks[idx];
      toast.success(`Block ${idx + 1} (#${b.from}–${b.to}, ${b.count} Briefe) → ${fmt(val)} · ${r.data.changed} neu gerendert`, { duration: 6000 });
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Datum konnte nicht gesetzt werden'); }
    finally { setBusy(''); }
  };

  if (!data) return <div className="px-4 py-2 text-[10px] text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Blöcke laden…</div>;
  const short = data.blocks.filter(b => b.count < size).length;
  return (
    <div className="px-4 py-2 border-b border-border bg-sky-500/5 space-y-1.5" data-testid="date-blocks-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <Layers className="w-3.5 h-3.5 text-sky-300 shrink-0" />
        <span className="text-[11px] text-foreground font-medium">Datum in Blöcken</span>
        <span className="text-[10px] text-muted-foreground font-mono" data-testid="date-blocks-total">
          {data.total} offene Erstansprache-Briefe in Schreibreihenfolge (alphabetisch je Serie)
        </span>
        <label className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
          Blockgröße
          <select value={size} onChange={e => setSize(Number(e.target.value))} data-testid="date-blocks-size"
            className="h-6 text-[10px] rounded-sm bg-secondary/40 border border-border px-1 text-foreground focus:outline-none">
            {SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <span className="ml-auto flex items-center gap-1.5 flex-wrap" data-testid="date-blocks-summary">
          {data.summary.map(s => (
            <span key={s.date || 'today'} data-testid={`date-summary-${s.date || 'today'}`}
              className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${s.date ? 'border-sky-500/40 bg-sky-500/10 text-sky-200' : 'border-border text-muted-foreground'}`}>
              {fmt(s.date)} × {s.count}
            </span>
          ))}
        </span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-1">
        {data.blocks.map(b => (
          <div key={b.index} className={`flex items-center gap-2 text-[10px] border rounded-sm px-2 py-1 ${b.count < size ? 'border-amber-500/40 bg-amber-500/5' : 'border-border/60'}`}
            data-testid={`date-block-${b.index}`}>
            <span className="font-mono text-foreground shrink-0">Block {b.index + 1}</span>
            <span className="font-mono text-muted-foreground shrink-0">#{b.from}–{b.to}</span>
            <span className={`font-mono shrink-0 ${b.count < size ? 'text-amber-300' : 'text-emerald-300'}`} data-testid={`date-block-count-${b.index}`}>{b.count} Briefe</span>
            <span className="text-muted-foreground truncate hidden md:inline">{b.first} … {b.last}</span>
            <span className="ml-auto flex items-center gap-1 shrink-0">
              {b.mixed && <span className="text-amber-300" title="Briefe in diesem Block tragen unterschiedliche Daten">gemischt</span>}
              <CalendarDays className="w-3 h-3 text-sky-300" />
              <input type="date" value={b.mixed ? '' : (b.date || '')} disabled={busy !== ''} data-testid={`date-block-input-${b.index}`}
                onChange={e => e.target.value && setBlock(b.index, e.target.value)}
                className="h-6 text-[10px] rounded-sm bg-secondary/40 border border-sky-500/40 px-1 text-foreground focus:outline-none" />
              {(b.date || b.mixed) && (
                <button onClick={() => setBlock(b.index, '')} disabled={busy !== ''} data-testid={`date-block-reset-${b.index}`}
                  className="px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground hover:text-foreground" title="Block zurück auf das heutige Datum">heute</button>
              )}
              {busy === String(b.index) && <Loader2 className="w-3 h-3 animate-spin text-sky-300" />}
            </span>
          </div>
        ))}
      </div>
      {short > 0 && (
        <p className="text-[10px] text-amber-300/90" data-testid="date-blocks-short-hint">
          {short === 1 ? 'Der letzte Block ist' : `${short} Blöcke sind`} kleiner als {size} — für eine volle Post-Charge fehlen Briefe (weitere freigeben oder mit dem nächsten Block zusammenlegen).
        </p>
      )}
    </div>
  );
};
